import { formatPlainDate } from '../../core/dates/plain-date';
import { useTranslation } from '../../core/hooks/use-translation';
import type { PortalAppointment } from '../../core/models/portal-appointment';
import { Badge } from '../../ui/badge';
import { CalendarIcon, ClockIcon, PinIcon, VideoIcon } from '../../ui/portal-icons';
import { AppointmentCancelForm } from './appointment-cancel-form';
import { APPOINTMENT_STATUS_KEYS } from './appointment-status-keys';
import { APPOINTMENT_STATUS_TONES } from './appointment-status-tones';

/**
 * One consultation.
 *
 * The date and time are printed as they arrived — already in the appointment's zone, converted by
 * the server. `scheduledAt` is not touched here: turning it into a local time would use the
 * handset's own zone database, which is the one thing this screen cannot rely on.
 *
 * A cancelled appointment is dimmed rather than dropped, because a customer who arrives to find one
 * missing has no way to tell it from one that was never made.
 *
 * The cancel control appears only where the server said `canCancel` and the caller can record the
 * outcome; the route checks the same rule again.
 *
 * The calendar tile is dropped on a telephone, where it took a sixth of the width from the service
 * name and repeated what the clock line already says.
 */
export function AppointmentCard({
    appointment,
    onCancelled
}: {
    appointment: PortalAppointment;
    onCancelled?: (appointmentId: string) => void;
}) {
    const { t, language } = useTranslation();

    const service = language === 'kk' ? appointment.serviceNameKk : appointment.serviceNameRu;
    const statusKey = APPOINTMENT_STATUS_KEYS[appointment.status] ?? 'portal.appointment_status.requested';
    const tone = APPOINTMENT_STATUS_TONES[appointment.status] ?? 'warning';
    const online = appointment.meetingFormat === 'Online';
    const cancelled = appointment.status === 'Cancelled';

    return (
        <li
            className={`flex gap-4 rounded-2xl border border-slate-200/80 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 ${
                cancelled ? 'opacity-65' : ''
            }`}
        >
            <span className="hidden size-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600 sm:flex dark:bg-brand-950 dark:text-brand-400">
                <CalendarIcon />
            </span>

            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="font-semibold text-slate-900 dark:text-slate-100">
                        {service ?? t('portal.appointments')}
                    </p>
                    <Badge tone={tone}>{t(statusKey)}</Badge>
                </div>

                <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
                    <span className="inline-flex items-center gap-1.5">
                        <ClockIcon className="size-4 text-slate-400" />
                        {appointment.scheduledDate === null || appointment.scheduledTime === null
                            ? t('portal.appointment_no_time')
                            : `${formatPlainDate(appointment.scheduledDate, language)} · ${appointment.scheduledTime}`}
                        {appointment.durationMinutes !== null &&
                            ` · ${appointment.durationMinutes} ${t('portal.duration_minutes')}`}
                    </span>

                    <span className="inline-flex items-center gap-1.5">
                        {online ? (
                            <VideoIcon className="size-4 text-slate-400" />
                        ) : (
                            <PinIcon className="size-4 text-slate-400" />
                        )}
                        {online
                            ? t('portal.format.online')
                            : `${t('portal.format.in_person')}${appointment.city === null ? '' : ` · ${appointment.city}`}`}
                    </span>
                </p>

                {appointment.canCancel && onCancelled !== undefined && (
                    <AppointmentCancelForm appointmentId={appointment.id} onCancelled={onCancelled} />
                )}
            </div>
        </li>
    );
}
